import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { PageLoader } from '../components/states';
import { AdminLayout, PublicLayout } from '../components/layouts';
import { useAuth } from '../features/auth/AuthContext';
import { AdminGuard } from '../features/auth/guards';
import { env } from '../lib/env';
import {
  AdminAuditPage,
  AdminCustomersPage,
  AdminDashboardPage,
  AdminOrderSyncPage,
  AdminPaymentsPage,
  AdminPlansPage,
  AdminRouteFallback,
  AdminSettingsPage,
  AdminSubscriptionDetailPage,
  AdminSubscriptionsPage,
} from '../pages/AdminPages';
import {
  AdminLoginPage,
  AuthExchangePage,
  LegalPage,
  NotFoundPage,
  PaymentResultPage,
} from '../pages/PublicPages';
import { AdminBrandingPage, AdminProductsPage } from '../pages/AdminCatalogPages';
import { CheckoutPage } from '../pages/CheckoutPage';

/**
 * Subscription management lives in the Tropikal Akdeniz customer account
 * ("Aboneliklerim"). Any old portal management URL — bookmarks, e-mails, the card-update
 * callback — lands here and is walked over to the account area.
 */
function AccountRedirect() {
  const target = `${env.shopifyStorefrontUrl}/account`;
  useEffect(() => {
    // The mock harness must stay on-origin: Playwright asserts the hand-off instead of
    // following a cross-site navigation to the live storefront.
    if (!env.useMockApi) window.location.replace(target);
  }, [target]);
  if (env.useMockApi) {
    return (
      <section className="page">
        <h1>Aboneliklerim taşındı</h1>
        <p>
          Aboneliklerinizi artık hesabınızdaki <strong>Aboneliklerim</strong> bölümünden
          yönetebilirsiniz.
        </p>
        <a href={target}>Aboneliklerim'e git</a>
      </section>
    );
  }
  return <PageLoader label="Hesabınıza yönlendiriliyorsunuz…" />;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user?.isAdmin) return <Navigate replace to="/admin" />;
  return <AccountRedirect />;
}

export function AppRouter() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route element={<HomeRedirect />} index />
        <Route element={<AuthExchangePage />} path="auth/exchange" />
        <Route element={<AdminLoginPage />} path="admin/login" />
        <Route element={<PaymentResultPage />} path="payment/result" />
        <Route element={<LegalPage type="subscription_terms" />} path="legal/subscription-terms" />
        <Route element={<LegalPage type="privacy_policy" />} path="legal/privacy" />
        <Route
          element={<LegalPage type="distance_sales_placeholder" />}
          path="legal/distance-sales"
        />
      </Route>

      {/*
       * The checkout renders its own full-page chrome (Shopify-matched header, two-column
       * layout, brand tokens), so it deliberately sits outside CustomerLayout.
       */}
      {/*
       * Deliberately outside CustomerGuard: subscribing does not require an account. The
       * single-use session token in the URL is the credential, and the page itself asks a
       * guest for the address their account will be created under.
       */}
      <Route element={<CheckoutPage />} path="checkout/:token" />

      {/* The old portal management surface: every variant forwards to Aboneliklerim. */}
      <Route element={<AccountRedirect />} path="subscriptions/*" />
      <Route element={<AccountRedirect />} path="subscriptions" />

      <Route element={<AdminGuard />}>
        <Route element={<AdminLayout />} path="admin">
          <Route element={<AdminDashboardPage />} index />
          <Route element={<AdminPlansPage />} path="plans" />
          <Route element={<AdminSubscriptionsPage />} path="subscriptions" />
          <Route element={<AdminSubscriptionDetailPage />} path="subscriptions/:id" />
          <Route element={<AdminPaymentsPage />} path="payments" />
          <Route element={<AdminOrderSyncPage />} path="order-sync" />
          <Route element={<AdminCustomersPage />} path="customers" />
          <Route element={<AdminAuditPage />} path="audit" />
          <Route element={<AdminProductsPage />} path="products" />
          <Route element={<AdminBrandingPage />} path="branding" />
          <Route element={<AdminSettingsPage />} path="settings" />
          <Route element={<AdminRouteFallback />} path="*" />
        </Route>
      </Route>

      <Route element={<PublicLayout />}>
        <Route element={<NotFoundPage />} path="*" />
      </Route>
    </Routes>
  );
}
